import { useEffect, useState } from 'react';

import {
  Box,
  Button as MuiButton,
  Card,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import ClaimOptionCard from '@/james-playground/aa-family-claim/ClaimOptionCard';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { type ReviewRow as IntakeRow } from '@/karehero-app/tell-us-once/intake/content';

/** A review row that can say where its answer came from. */
export type ReviewRow = IntakeRow & {
  /**
   * Where the answer came from, and the words it was drawn from. Shown under
   * the answer as a line that opens the quote, so a suggested answer can be
   * checked against what was actually said or written.
   */
  source?: { label: string; quote?: string };
};

/**
 * The Attendance Allowance prototype's copy of
 * `karehero-app/tell-us-once/intake/FactList`,
 * so Vez's shared component, and every screen that uses it, stays exactly as
 * she built it.
 *
 * What differs: rows can show their source and its quote; long text edits in a
 * full-width multiline field; the pencil is a labelled button, so the row is
 * no longer a control wrapped round another control; and the drawer's save
 * calls `onChange`, which the shared list does not.
 *
 * These are proposals for the shared component: fold them back, or delete
 * this copy, once she has looked.
 */

// One card per group, rows hairline-separated inside it, the question above its
// answer and the whole row the edit target. Taken from F9 of the Tell Us Once
// exploration, which is tighter than a card per fact and still reads as grouped.
//
// Shared by both options' review screens and the profile, because a grouped list
// of facts is the same object in all three and was drawn three times before this.
export type FactGroup = {
  label: string;
  rows: ReviewRow[];
  /**
   * Answered and not worth a person's attention, so collapsed to a count. This
   * is what keeps a 66-question form finite: the review shows what needs someone
   * and counts the rest.
   *
   * They open in a drawer as the same rows, editable the same way, so the drawer
   * doubles as the place to come back and read a section later.
   */
  hidden?: ReviewRow[];
  /** A line under the group, for something the rows cannot say. */
  note?: string;
};

/**
 * Opens the editor like an accordion, but only where there is something to
 * open.
 *
 * A name or an address is four fields where the row was one line, and arriving
 * at full height reads as the page jumping. A text field, a select and a date
 * land on the line the answer was already on, and animating those is a swap
 * pretending to be a movement: the field appears to hesitate before you can
 * type in it.
 */
const Grow = ({
  grows,
  children,
}: {
  grows: boolean;
  children: React.ReactNode;
}) =>
  grows ? (
    <Collapse in appear>
      {children}
    </Collapse>
  ) : (
    <>{children}</>
  );

/**
 * The width kept for a row's control, and the height it is centred in.
 *
 * Reserved whether the row is open or not. The pencil, the X and the Save all
 * live in here, so pressing edit swaps what is in the slot and moves nothing:
 * the slot does not widen, the value beside it does not reflow, and the height
 * is the closed row's two lines, so a four-field address grows underneath
 * rather than dragging Save down with it.
 */
const CONTROL_SLOT = { width: 112, height: 40 };

/**
 * The answer, as the thing it actually is.
 *
 * Every row used to open as a text field, which is fine for a phone number and
 * wrong for the rest: a nationality is one of a list, a date of birth is a
 * date, and a name and an address are several fields that a single line makes
 * you edit by retyping the middle of a sentence.
 *
 * In place rather than in a sheet. The member app lifts these into a
 * BottomSheet, which is right on a phone and would be a modal over a drawer
 * here. So the row grows instead, and Collapse does the growing.
 */
const RowEditor = ({
  row,
  draft,
  setDraft,
  parts,
  setParts,
  onEnter,
  onEscape,
  onBlur,
}: {
  row: ReviewRow;
  draft: string;
  setDraft: (v: string) => void;
  parts: Record<string, string>;
  setParts: (v: Record<string, string>) => void;
  onEnter: () => void;
  onEscape: () => void;
  /**
   * Leaving the control saves it, where the caller saves on blur.
   *
   * Never passed for `parts`: tabbing from the first line of an address to the
   * second is leaving a control, and it is not the same thing as being finished
   * with the address.
   */
  onBlur?: () => void;
}) => {
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onEnter();
    if (e.key === 'Escape') onEscape();
  };

  if (row.control === 'choice' && row.choices) {
    return (
      <Select
        autoFocus
        fullWidth
        size='small'
        variant='standard'
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={onBlur}
        onKeyDown={keys}
      >
        {row.choices.map((choice) => (
          <MenuItem key={choice} value={choice}>
            {choice}
          </MenuItem>
        ))}
      </Select>
    );
  }

  if (row.control === 'date') {
    return (
      <TextField
        autoFocus
        fullWidth
        size='small'
        variant='standard'
        type='date'
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={keys}
      />
    );
  }

  if (row.control === 'parts' && row.parts) {
    return (
      // Labelled, because "14 Brae Street" over "Edinburgh" over "EH3 6HR" is
      // only obvious to somebody who already knows the address.
      <Stack sx={{ pt: 1 }} spacing={2}>
        {row.parts.map((part, i) => (
          <TextField
            key={part.label}
            autoFocus={i === 0}
            fullWidth
            size='small'
            variant='standard'
            label={part.label}
            value={parts[part.label] ?? ''}
            onChange={(e) =>
              setParts({ ...parts, [part.label]: e.target.value })
            }
            onKeyDown={keys}
          />
        ))}
      </Stack>
    );
  }

  // A sentence edits as a sentence: on a line it scrolls sideways under the
  // cursor, and the words being corrected are the ones that scroll out of view.
  const long = draft.length > 40;
  return (
    <TextField
      autoFocus
      fullWidth
      size='small'
      variant='standard'
      multiline={long}
      minRows={long ? 2 : undefined}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={onBlur}
      onKeyDown={keys}
      slotProps={{ htmlInput: { 'aria-label': row.label } }}
    />
  );
};

/**
 * Where a suggested answer came from, as a line that opens the words it was
 * drawn from. A sparkle says an answer was read rather than typed; this says
 * what it was read from, so it can be checked.
 */
const SourceLine = ({
  source,
}: {
  source: NonNullable<ReviewRow['source']>;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ pt: 0.5 }} onClick={(e) => e.stopPropagation()}>
      {source.quote ? (
        <MuiButton
          variant='text'
          size='small'
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          sx={{ p: 0, minWidth: 0, justifyContent: 'flex-start' }}
        >
          {source.label}
        </MuiButton>
      ) : (
        <Typography sx={{ color: 'text.secondary' }} variant='caption'>
          {source.label}
        </Typography>
      )}
      {open && source.quote && (
        <Typography
          sx={{
            mt: 0.5,
            pl: 1.5,
            borderLeft: 2,
            borderColor: 'custom.sunflower.500',
            color: 'text.primary',
            fontStyle: 'italic',
          }}
          variant='body2'
        >
          “{source.quote}”
        </Typography>
      )}
    </Box>
  );
};

const Row = ({
  row,
  first,
  open,
  onOpen,
  onClose,
  value,
  resolved,
  explicit,
  onSettle,
}: {
  row: ReviewRow;
  first: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** The current answer, which is an edit if one has been made. */
  value: string;
  resolved: boolean;
  /**
   * The row is saved by pressing Save on it, not by leaving it.
   *
   * On the member's own review, settling a row is the whole act and blur is a
   * fine way to say so. A care expert is editing somebody else's record, where
   * a click that lands somewhere else should not be what writes a phone number
   * down.
   */
  explicit?: boolean;
  onSettle: (value: string) => void;
}) => {
  // A date edits as yyyy-mm-dd and a name edits as its fields, so the draft is
  // whichever of those the row takes. `value` stays the line the list shows.
  const [draft, setDraft] = useState(
    row.control === 'date' ? (row.iso ?? '') : value,
  );
  const [parts, setParts] = useState<Record<string, string>>(() =>
    Object.fromEntries((row.parts ?? []).map((p) => [p.label, p.value])),
  );

  // What the answer reads as once it is saved. Parts join with the row's own
  // separator, empty ones dropped: an address with no second line should not
  // save with a hole in it.
  const composed = () => {
    if (row.control === 'parts' && row.parts) {
      return row.parts
        .map((p) => parts[p.label]?.trim())
        .filter(Boolean)
        .join(row.join ?? ', ');
    }
    if (row.control === 'date' && draft) {
      return new Date(draft).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
    return draft;
  };

  // Red is three states and each clears differently. Resolving turns the row
  // plain: never sunflower, because sunflower means nobody has checked it.
  const kind = resolved ? undefined : row.from;
  const red = kind === 'missing' || kind === 'conflict' || kind === 'stale';
  const ai = kind === 'ai';

  // A conflict resolves in the row, unopened: two answers have to be visible for
  // it to be obvious one has to go. A stale value only carries a warning, since
  // the number is not in doubt, only its date, and it is edited like any other.
  const choosing = kind === 'conflict';
  const checking = kind === 'stale';
  const typing = open && !choosing;

  // A conditional question nobody has answered yet may never need answering,
  // so the row says less: no Not answered, and the whole thing dimmed. Once it
  // has an answer it was clearly asked, and it reads like any other row.
  const unasked = Boolean(row.conditional) && !value && !typing;

  // Some answers save explicitly whatever the list's mode is. A name and an
  // address are several fields, so leaving one of them is not being finished
  // with the answer; a date picker takes focus away to show its calendar. Both
  // would save and close under blur, halfway through being filled in.
  const explicitRow =
    explicit || row.control === 'parts' || row.control === 'date';

  // Whether opening the row makes it taller. A text field, a select and a date
  // all land on the line the answer was already on, so animating them is a
  // swap pretending to be a movement. Only the several-field answers grow, and
  // those are the ones worth opening like an accordion.
  const grows = row.control === 'parts';
  // A text answer edits across the whole row. Beside the control slot it had a
  // third of the width, and a sentence scrolled sideways inside it.
  const wide = typing && !explicitRow && (row.control ?? 'text') === 'text';

  const settle = (next: string) => {
    onSettle(next);
    onClose();
  };

  // Put the row back as it was. Escape and the X are the same act, so they are
  // the same function: an open row that is abandoned should leave nothing
  // behind, including a draft that reappears the next time it is opened.
  const cancel = () => {
    setDraft(row.control === 'date' ? (row.iso ?? '') : value);
    setParts(
      Object.fromEntries((row.parts ?? []).map((p) => [p.label, p.value])),
    );
    onClose();
  };

  return (
    <Box
      onClick={open || choosing ? undefined : onOpen}
      sx={{
        px: 2,
        py: 1.5,
        borderTop: first ? 0 : 1,
        borderColor: 'custom.neutral.200',
        bgcolor: red ? 'custom.coral.50' : undefined,
        cursor: open || choosing ? 'default' : 'pointer',
        opacity: unasked ? 0.6 : 1,
      }}
    >
      <Stack
        sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
        direction='row'
        spacing={2}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={0.75}>
            {(red || ai) && (
              <Box
                sx={{
                  color: red ? 'custom.coral.600' : 'custom.sunflower.700',
                  display: 'flex',
                }}
              >
                <Icon
                  icon={(red ? 'circle-exclamation' : 'sparkles') as never}
                  fontSize={11}
                />
              </Box>
            )}
            <Typography sx={{ color: 'text.secondary' }} variant='body2'>
              {row.label}
            </Typography>
          </Stack>

          {/* Every row is edited the same way, wherever it is: tap it and the
              answer becomes the input. A missing one has nothing else it could
              be. */}
          {wide ? null : typing ? (
            <Grow grows={grows}>
              <RowEditor
                row={row}
                draft={draft}
                setDraft={setDraft}
                parts={parts}
                setParts={setParts}
                onEnter={() => settle(composed())}
                // Escape only puts a row back where leaving it does not save
                // it. Where blur is the save there is nothing to escape from.
                onEscape={explicitRow ? cancel : onClose}
                onBlur={
                  explicitRow ? undefined : () => draft && settle(composed())
                }
              />
            </Grow>
          ) : choosing ? (
            <Typography sx={{ color: 'custom.coral.700' }} variant='body2'>
              Two sources disagree. Pick one.
            </Typography>
          ) : unasked ? null : (
            <Typography
              sx={{ color: red ? 'custom.coral.700' : 'text.primary' }}
              variant='h6'
            >
              {value || 'Not answered'}
            </Typography>
          )}

          {/* Both readings, in the row rather than behind a tap: one of them has
              to go, and that is only obvious if you can see both. The loser stays
              in the history, because the disagreement is a fact too. */}
          {/* The value is fine, its date is not. The warning says so and stops
              there: a confirm here would claim someone had checked the balance
              when they had only recognised the number. */}
          {checking && !typing && (
            <Typography
              sx={{ pt: 0.5, color: 'custom.coral.700' }}
              variant='caption'
            >
              This came from {row.since}.
            </Typography>
          )}

          {/* Why the question might not apply, as a chip. The schema says it
              in a sentence ("Only asked if the answer above is Yes"), which is
              a line of prose per row on a form of 227 of them. Which answer
              opens it is in the form; that it depends on one is what a care
              expert working down the list needs. */}
          {row.conditional && (
            <Box sx={{ pt: 0.75 }}>
              <Chip
                label='Conditional on previous answer'
                size='small'
                variant='outlined'
              />
            </Box>
          )}

          {/* Something true of the question. Secondary rather than coral: a
              conditional question is not a problem with the answer. */}
          {row.note && (
            <Typography
              sx={{ pt: 0.5, color: 'text.secondary' }}
              variant='caption'
            >
              {row.note}
            </Typography>
          )}

          {row.source && !typing && <SourceLine source={row.source} />}
        </Box>

        {/* No pencil on a conflict: the row carries its own answers. The slot
            is still reserved, so a conflict row lines up with the rest. */}
        <Box
          sx={{
            ...CONTROL_SLOT,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          {choosing ? null : typing && explicitRow ? (
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={3}>
              <IconButton aria-label='Discard' size='small' onClick={cancel}>
                <Icon icon={'xmark' as never} fontSize={13} />
              </IconButton>
              {/* Never disabled. A Save you cannot press is a control that has
                  decided the row is fine, and it leaves you pressing it to find
                  out why nothing happened. Saving the same answer twice costs
                  nothing; clearing one is how a fact becomes a gap. */}
              <MuiButton
                variant='text'
                color='primary'
                size='small'
                onClick={() => settle(composed())}
              >
                Save
              </MuiButton>
            </Stack>
          ) : (
            // The pencil is the row's control for a keyboard or screen
            // reader: a real button, named for the answer it edits. A tap
            // anywhere on the row still opens it for everyone else.
            <IconButton
              size='small'
              aria-label={`Edit ${row.label}: ${value || 'Not answered'}`}
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
              sx={{
                color: red ? 'custom.coral.600' : 'custom.neutral.600',
                // Level with Save's own text, so the pencil sits where the word
                // will be rather than hard against the row's edge.
                mr: 0,
              }}
            >
              <Icon icon={'pencil' as never} fontSize={13} />
            </IconButton>
          )}
        </Box>
      </Stack>

      {wide && (
        <Box sx={{ pt: 1 }}>
          <RowEditor
            row={row}
            draft={draft}
            setDraft={setDraft}
            parts={parts}
            setParts={setParts}
            onEnter={() => settle(composed())}
            onEscape={onClose}
            onBlur={() => draft && settle(composed())}
          />
        </Box>
      )}

      {/* Both readings, across the row rather than in the column beside the
          control slot. They are the answer here, not a note about it: squeezed
          into two thirds of the row they read as a caption on the fact above
          them, and one of them is about to become the fact. */}
      {choosing && (
        <Stack sx={{ pt: 1.5 }} spacing={1.5}>
          {row.options?.map((option) => (
            <ClaimOptionCard
              key={option.value}
              control='radio'
              selected={false}
              label={option.value}
              onClick={() => settle(option.value)}
            >
              <Typography sx={{ color: 'text.secondary' }} variant='caption'>
                {option.source}
              </Typography>
            </ClaimOptionCard>
          ))}
        </Stack>
      )}
    </Box>
  );
};

// One row, opened on its own, with the control its answer takes. This is what
// the drawer is for in the expanding list: the group is revealed in place, and
// the sheet is kept for the thing a row cannot do — a pick-one or a
// pick-several needs room and a set of options, and neither fits in a row that
// is also showing its answer.
//
// Every control commits on Done rather than on the tap. A pick-one that closed
// the sheet the moment you touched it would make changing your mind mid-sheet
// impossible, and a pick-several has no single tap to close on.
const RowSheet = ({
  row,
  value,
  onSettle,
  onClose,
}: {
  row: ReviewRow;
  value: string;
  onSettle: (value: string) => void;
  onClose: () => void;
}) => {
  const [draft, setDraft] = useState(
    row.control === 'date' ? (row.iso ?? '') : value,
  );
  const [parts, setParts] = useState<Record<string, string>>(() =>
    Object.fromEntries((row.parts ?? []).map((p) => [p.label, p.value])),
  );
  const picked = draft ? draft.split(', ').filter(Boolean) : [];

  // What the answer reads as once it is saved. The same rule the row uses when
  // it edits in place: parts join with the row's own separator, a date reads as
  // a date rather than as the input's value.
  const composed = () => {
    if (row.control === 'parts' && row.parts) {
      return row.parts
        .map((p) => parts[p.label]?.trim())
        .filter(Boolean)
        .join(row.join ?? ', ');
    }
    if (row.control === 'date' && draft) {
      return new Date(draft).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
    return draft;
  };

  const toggle = (choice: string) =>
    setDraft(
      (picked.includes(choice)
        ? picked.filter((p) => p !== choice)
        : [...picked, choice]
      ).join(', '),
    );

  return (
    <BottomSheet
      variant='hug'
      title={row.label}
      onClose={onClose}
      footer={(close) => (
        <MuiButton
          variant='contained'
          size='large'
          fullWidth
          onClick={() => {
            onSettle(composed());
            close();
          }}
        >
          Done
        </MuiButton>
      )}
    >
      {/* Every field in here is the same field: filled, labelled, full width,
          which is what the theme gives a TextField and what the address's four
          already were. The date and the free-text one were unlabelled, so the
          drawer had two kinds of input in it depending on which row opened it.

          A label as well as the sheet's title, because a labelled field is what
          the rest of the app's forms look like and the title is the sheet's
          rather than the field's. */}
      {row.control === 'parts' && row.parts ? (
        // A name is a title and two names, an address is three lines and a
        // postcode. One field for either of those makes an edit a retype of the
        // middle of a sentence.
        <Stack spacing={2}>
          {row.parts.map((part, i) => (
            <TextField
              key={part.label}
              autoFocus={i === 0}
              fullWidth
              size='small'
              label={part.label}
              value={parts[part.label] ?? ''}
              onChange={(e) =>
                setParts({ ...parts, [part.label]: e.target.value })
              }
            />
          ))}
        </Stack>
      ) : row.control === 'date' ? (
        <TextField
          autoFocus
          fullWidth
          size='small'
          type='date'
          label={row.label}
          slotProps={{ inputLabel: { shrink: true } }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      ) : row.control === 'choice' || row.control === 'multi' ? (
        <Stack spacing={2}>
          {row.choices?.map((choice) => (
            <ClaimOptionCard
              key={choice}
              control={row.control === 'multi' ? 'check' : 'radio'}
              selected={
                row.control === 'multi'
                  ? picked.includes(choice)
                  : draft === choice
              }
              label={choice}
              onClick={() =>
                row.control === 'multi' ? toggle(choice) : setDraft(choice)
              }
            />
          ))}
        </Stack>
      ) : (
        <TextField
          autoFocus
          fullWidth
          size='small'
          label={row.label}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      )}
    </BottomSheet>
  );
};

const ClaimFactList = ({
  groups,
  hideUnanswered = false,
  confirmed = false,
  initialEditing,
  initialExpanded,
  edit = 'inline',
  onChange,
  save = 'blur',
  collapsible = false,
  onUnresolved,
  revealUnresolved = 0,
}: {
  groups: FactGroup[];
  /**
   * Drop the facts nothing answered. Review is a read of what we hold, so a fact
   * with no answer has nothing to review; the profile keeps them, because a gap
   * is something the profile is for.
   */
  hideUnanswered?: boolean;
  /**
   * A person has been through the list and said it reads right, so the rows we
   * read out of what they sent are no longer unchecked and lose their sparkle.
   *
   * Only those. Saying the list reads right does not fill a gap, settle a
   * disagreement or freshen a stale figure, so missing, conflict and stale rows
   * are untouched: they still need something nobody has done yet.
   */
  confirmed?: boolean;
  /**
   * Harness-only: open this row for editing on first paint, so a flows page can
   * show what editing one kind of answer looks like without anybody tapping.
   */
  initialEditing?: string;
  /** Harness-only: start with this group's count already expanded. */
  initialExpanded?: string;
  /**
   * Where a row is changed. 'inline' turns the answer into a text field, which
   * only works while every answer is text. 'drawer' opens the row on its own,
   * with the control its answer actually takes — a text field, a pick-one or a
   * pick-several.
   */
  edit?: 'inline' | 'drawer';
  /**
   * A row settled on a new answer.
   *
   * For a caller that holds a Save. The member's review has none: settling a
   * row there is the whole act, and the list is the record. The CAM drawers
   * collect these and write them down on Save, so they need to be told.
   */
  onChange?: (id: string, value: string) => void;
  /**
   * What writes a row down. 'blur' settles it when you leave it, which is the
   * member's review. 'explicit' puts a small Save in the row and leaves the
   * answer alone until it is pressed, which is what the CAM drawers use.
   */
  save?: 'blur' | 'explicit';
  /**
   * Sections fold shut, and start that way apart from the first.
   *
   * For a list long enough that its section names are the way around it.
   * Attendance Allowance is fourteen sections and 227 questions, and a care
   * expert filling it in on somebody's behalf works a section at a time. A
   * profile of four groups of four rows has nothing to fold.
   */
  collapsible?: boolean;
  /**
   * How many answers two sources still disagree about.
   *
   * Only clashes. Nothing here is required, so a gap and a stale figure are
   * both things somebody may reasonably leave; a clash is the one state where
   * carrying on sends something we already know is wrong.
   *
   * Reported rather than left for the caller to work out, because whether a row
   * still counts depends on the edits this list is holding.
   */
  onUnresolved?: (count: number) => void;
  /**
   * Bumped to put the first row that still needs somebody on screen.
   *
   * A nonce rather than a boolean, so pressing the confirm a second time takes
   * you back to it. The list does the scrolling because the list is what knows
   * which row that is and where it ended up.
   */
  revealUnresolved?: number;
}) => {
  const [editing, setEditing] = useState<string | null>(initialEditing ?? null);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(initialExpanded ? [initialExpanded] : []),
  );
  // Which sections are open, where they fold at all. The first one is, so the
  // list opens on something rather than on a column of headings.
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(collapsible && groups[0] ? [groups[0].label] : []),
  );

  // Answers and resolutions live here rather than in the row, so an edit made in
  // the drawer is still there when the drawer is reopened.
  const [edits, setEdits] = useState<Record<string, string>>({});

  const toggleGroup = (label: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (!next.delete(label)) next.add(label);
      return next;
    });

  // A row is answered when it has something in it, counting an edit made in
  // this drawer: a care expert who has just typed an answer should see the
  // count move.
  const answeredIn = (rows: ReviewRow[]) =>
    rows.filter((row) => (edits[row.id] ?? row.value) !== '').length;

  const shown = groups
    .map((group) => ({
      ...group,
      // Counted off the whole section, before hideUnanswered takes rows out of
      // it: what is left to answer is the point of the count, so a hidden
      // unanswered row still has to be in the total. Hidden rows are answered
      // by definition, which is why they are collapsed.
      answered: answeredIn(group.rows) + (group.hidden?.length ?? 0),
      total: group.rows.length + (group.hidden?.length ?? 0),
      rows: hideUnanswered
        ? group.rows.filter((row) => row.from !== 'missing')
        : group.rows,
    }))
    .filter((group) => group.rows.length > 0 || group.hidden?.length);

  // The row the sheet is showing, found across both visible and hidden rows:
  // in expand mode a hidden row is on screen like any other, so it has to be
  // openable like any other.
  const editingRow =
    edit === 'drawer' && editing
      ? groups
          .flatMap((group) => [...group.rows, ...(group.hidden ?? [])])
          .find((row) => row.id === editing)
      : undefined;

  // Keyed on the current answer so an edited row remounts and its draft input
  // starts from what the row now says, not from what it said at first paint.
  const rowKey = (row: ReviewRow) => `${row.id}:${edits[row.id] ?? ''}`;

  // Only a clash. Nothing on this list is required: a gap is a gap and a stale
  // figure is still a figure, and both can be left. Two sources disagreeing is
  // the one state where carrying on means sending something we know is wrong,
  // because one of the two answers is false and we do not know which.
  const unresolved = groups
    .flatMap((group) => [...group.rows, ...(group.hidden ?? [])])
    .filter((row) => !(row.id in edits) && row.from === 'conflict').length;

  useEffect(() => {
    onUnresolved?.(unresolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unresolved]);

  // Take them to the first one. Being told two things disagree is no use if
  // both are below the fold, and the top of the list is not where they are.
  useEffect(() => {
    if (!revealUnresolved) return;
    const first = groups
      .flatMap((group) => [...group.rows, ...(group.hidden ?? [])])
      .find((row) => !(row.id in edits) && row.from === 'conflict');
    if (!first) return;
    document
      .getElementById(`fact-${first.id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealUnresolved]);

  const rowProps = (row: ReviewRow) => ({
    row,
    // In drawer mode the row never becomes an input: it stays a row and the
    // sheet below is what opens.
    open: edit === 'inline' && editing === row.id,
    onOpen: () => setEditing(row.id),
    onClose: () => setEditing(null),
    value: edits[row.id] ?? row.value,
    resolved: row.id in edits || (confirmed && row.from === 'ai'),
    explicit: save === 'explicit',
    onSettle: (value: string) => {
      setEdits((prev) => ({ ...prev, [row.id]: value }));
      onChange?.(row.id, value);
    },
  });

  return (
    <>
      <Stack spacing={3}>
        {shown.map((group) => {
          const folded = collapsible && !openGroups.has(group.label);
          return (
            <Stack key={group.label} spacing={1}>
              {/* The heading is the control where sections fold: a chevron and
                the name, with how much of the section is answered on the far
                right. A shut section still has to say whether there is
                anything in it, which is what a care expert is scanning the
                headings for. */}
              <Stack
                onClick={
                  collapsible ? () => toggleGroup(group.label) : undefined
                }
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: collapsible ? 'pointer' : 'default',
                }}
                direction='row'
                spacing={1}
              >
                <Stack
                  sx={{ alignItems: 'center', minWidth: 0 }}
                  direction='row'
                  spacing={1}
                >
                  {collapsible && (
                    <Box
                      sx={{
                        color: 'text.secondary',
                        display: 'flex',
                        transition: 'transform 150ms ease',
                        transform: folded ? 'none' : 'rotate(90deg)',
                      }}
                    >
                      <Icon icon={'chevron-right' as never} fontSize={11} />
                    </Box>
                  )}
                  <Typography
                    sx={{ color: 'text.secondary' }}
                    variant='overline'
                  >
                    {group.label}
                  </Typography>
                </Stack>

                {/* Only where the sections fold. A profile of four short
                    groups is read, not worked through, and a count on it is a
                    number nobody needs. */}
                {collapsible && (
                  <Typography
                    sx={{ color: 'text.secondary', flexShrink: 0 }}
                    variant='overline'
                  >
                    {group.answered}/{group.total}
                  </Typography>
                )}
              </Stack>

              <Collapse in={!folded} unmountOnExit>
                <Card sx={{ p: 0, overflow: 'hidden' }}>
                  {/* The id is how the list finds a row again: told that
                      something still needs them, somebody has to be taken to
                      it. */}
                  {group.rows.map((row, i) => (
                    <Box key={rowKey(row)} id={`fact-${row.id}`}>
                      <Row {...rowProps(row)} first={i === 0} />
                    </Box>
                  ))}

                  {/* Revealed in place, under the rows they were hidden
                      behind, so the list stays one object. Lifting them into a
                      sheet made them a different thing from the rows above
                      them, which they are not. */}
                  {expanded.has(group.label) &&
                    group.hidden?.map((row) => (
                      <Box key={rowKey(row)} id={`fact-${row.id}`}>
                        <Row {...rowProps(row)} first={false} />
                      </Box>
                    ))}

                  {/* The control sits under whatever it is showing, so it is
                      where you have read to rather than where you started, and
                      it says what the tap does now: Collapse once they are
                      open, the count while they are not. */}
                  {group.hidden?.length ? (
                    <Box
                      onClick={() =>
                        setExpanded((prev) => {
                          const next = new Set(prev);
                          if (!next.delete(group.label)) next.add(group.label);
                          return next;
                        })
                      }
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderTop:
                          group.rows.length === 0 && !expanded.has(group.label)
                            ? 0
                            : 1,
                        borderColor: 'custom.neutral.200',
                        cursor: 'pointer',
                      }}
                    >
                      <Stack
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                        direction='row'
                        spacing={2}
                      >
                        <Typography
                          sx={{ color: 'primary.main' }}
                          variant='body2'
                        >
                          {expanded.has(group.label)
                            ? 'Collapse'
                            : `${group.hidden.length} more, all answered`}
                        </Typography>
                        {/* Points at where the rows will go: down to open them,
                            up to take them away again. */}
                        <Box
                          sx={{
                            color: 'primary.main',
                            display: 'flex',
                            transition: 'transform 150ms ease',
                            transform: expanded.has(group.label)
                              ? 'rotate(180deg)'
                              : 'none',
                          }}
                        >
                          <Icon icon={'chevron-down' as never} fontSize={12} />
                        </Box>
                      </Stack>
                    </Box>
                  ) : null}
                </Card>

                {group.note && (
                  <Typography
                    sx={{ color: 'text.secondary' }}
                    variant='caption'
                  >
                    {group.note}
                  </Typography>
                )}
              </Collapse>
            </Stack>
          );
        })}
      </Stack>

      {editingRow && (
        <RowSheet
          key={editingRow.id}
          row={editingRow}
          value={edits[editingRow.id] ?? editingRow.value}
          onSettle={(value) => {
            setEdits((prev) => ({ ...prev, [editingRow.id]: value }));
            onChange?.(editingRow.id, value);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
};

export default ClaimFactList;
